import { Checkbox } from '@nextui-org/react'
import { useCallback, useEffect, useState } from 'react'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import { AgentRequest } from '~/protos/agents'
import {
  buildCommandContextUploadFromApiItems,
  type ContextUploadApiItem
} from '~/types/contextUpload'
import { BaseTaskForm, TaskParams } from './BaseTaskForm'
import { InfoTooltip } from './shared/InfoToolTip'
import {
  SupportingFilesUpload,
  type SupportingFilesUploadState
} from './shared/SupportingFilesUpload'

interface MuleVersionUpgradeFormProps {
  onChange: (task: TaskParams) => void
  withExamplePrefilled?: boolean
  initialTaskData?: TaskParams | null
  initVmArgs?: string
  suggestedVmArgs?: string
  isLoadingSuggestedVmArgs?: boolean
  hideVmArgs?: boolean
  disabled?: boolean
  runVerificationDisabled?: boolean
  runVerification?: boolean
  onRunVerificationChange?: (value: boolean) => void
}

export function MuleVersionUpgradeForm({
  onChange,
  withExamplePrefilled,
  initialTaskData,
  initVmArgs = '',
  suggestedVmArgs = '',
  isLoadingSuggestedVmArgs = false,
  hideVmArgs = false,
  disabled = false,
  runVerificationDisabled = false,
  runVerification = false,
  onRunVerificationChange
}: MuleVersionUpgradeFormProps) {
  // Initialize form state
  const getInitialDescription = () => {
    if (initialTaskData?.request?.command?.commandString) {
      return initialTaskData.request.command.commandString
    }
    if (withExamplePrefilled) {
      return `Upgrade Mule application from current runtime to target runtime version.
Ensure compatibility and update deprecated components.`.trim()
    }
    return ''
  }

  const [description, setDescription] = useState(getInitialDescription())
  const [vmArgs, setVMArgs] = useState(initialTaskData?.request?.vmArgs || initVmArgs)
  const [contextUploadData, setContextUploadData] = useState<ContextUploadApiItem[]>([])
  const [submitBlockedReason, setSubmitBlockedReason] = useState<string | undefined>()

  // Update VM args when initVmArgs changes
  useEffect(() => {
    if (!initialTaskData) {
      setVMArgs(initVmArgs)
    }
  }, [initVmArgs, initialTaskData])

  const handleSupportingFilesChange = useCallback(
    ({ contextUploadData, hasPendingUploads, hasFailedUploads }: SupportingFilesUploadState) => {
      setContextUploadData(contextUploadData)
      setSubmitBlockedReason(
        hasPendingUploads
          ? 'Please wait for all supporting files to finish uploading.'
          : hasFailedUploads
            ? 'Some supporting file uploads failed. Please remove them and try again.'
            : undefined
      )
    },
    []
  )

  useEffect(() => {
    const request = AgentRequest.fromPartial({
      command: {
        commandString: description,
        contextUpload: buildCommandContextUploadFromApiItems(contextUploadData)
      },
      vmArgs
      // Both oldRuntimeSettings and runtimeSettings will be populated by TaskForm
    })
    onChange?.({ request, submitBlockedReason })
  }, [contextUploadData, description, onChange, submitBlockedReason, vmArgs])

  return (
    <div className='flex flex-col'>
      <BaseTaskForm
        onChange={({ request }) => setDescription(request.command?.commandString || '')}
        label='Description'
        defaultValue={getInitialDescription()}
        placeholder='Describe the Mule version upgrade requirements...'
        disabled={disabled}
        required={false}
      />

      <div className='mt-4'>
        <SupportingFilesUpload onChange={handleSupportingFilesChange} disabled={disabled} />
      </div>

      <div>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
          <Checkbox
            isSelected={runVerification}
            onChange={e => onRunVerificationChange?.(e.target.checked)}
            isDisabled={runVerificationDisabled}
          >
            <span className='font-semibold text-xs text-default-700 select-none'>
              Run Verification
            </span>
          </Checkbox>
          <InfoTooltip content='Enable thorough verification and validation of the upgraded code' />
        </div>
        <div className='text-xs text-default-500 ml-6 mb-[10px] select-none'>
          Compiles, deploys, tests, and auto-fixes errors
        </div>
      </div>

      {!hideVmArgs && (
        <div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
            <div className='flex items-center gap-2'>
              <span>VM Args:</span>
              {isLoadingSuggestedVmArgs && (
                <div className='flex items-center gap-1 text-xs text-gray-500 select-none'>
                  <svg className='animate-spin h-3 w-3' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                      fill='none'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Loading VM args suggestions from repository...</span>
                </div>
              )}
            </div>
            <InfoTooltip content='VM arguments needed for testing the upgraded application in the target runtime environment' />
          </div>
          <SimpleTextarea
            className='mx-0'
            value={vmArgs}
            onChange={setVMArgs}
            minRows={2}
            placeholder={suggestedVmArgs || ''}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  )
}
